import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowRight,
  Globe,
  Smartphone,
  ShoppingCart,
  Star,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';
import {
  services,
  stats,
  testimonials,
  portfolioProjects,
  companyInfo
} from '../data/mock';

const iconMap = {
  Globe: Globe,
  Smartphone: Smartphone,
  ShoppingCart: ShoppingCart
};

const Home = () => {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/2 -left-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse delay-500" />
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-blue-300 text-sm font-medium">{t('hero.badge')}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero.title1')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                {t('hero.title2')}
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/kontakt">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg group">
                  {t('hero.cta1')}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  {t('buttons.viewAll')} Portfolio
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8">
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-white mb-1">150+</div>
                <div className="text-slate-400 text-sm">{t('stats.projectsLabel')}</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-white mb-1">98%</div>
                <div className="text-slate-400 text-sm">{t('stats.satisfactionLabel')}</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-white mb-1">10+</div>
                <div className="text-slate-400 text-sm">{t('stats.experienceLabel')}</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-3xl font-bold text-white mb-1">50+</div>
                <div className="text-slate-400 text-sm">{t('stats.clientsLabel')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('home.servicesTitle')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">
              {t('home.servicesHeading')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('home.servicesDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              const serviceKey = service.id === 1 ? 'websites' : service.id === 2 ? 'webapps' : 'ecommerce';
              return (
                <Card key={service.id} className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                      {IconComponent && (
                        <IconComponent className="text-blue-600 group-hover:text-white transition-colors duration-300" size={28} />
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{t(`servicesCards.${serviceKey}.title`)}</h3>
                    <p className="text-slate-600 mb-6">{t(`servicesCards.${serviceKey}.desc`)}</p>
                    <ul className="space-y-2 mb-6">
                      {[1,2,3,4].map((num) => (
                        <li key={num} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="text-green-500" size={16} />
                          {t(`servicesCards.${serviceKey}.f${num}`)}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/leistungen"
                      className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 group/link"
                    >
                      {t('buttons.learnMore')}
                      <ChevronRight className="ml-1 group-hover/link:translate-x-1 transition-transform" size={18} />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12">
            <div>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{t('home.portfolioTitle')}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
                {t('home.portfolioSubtitle')}
              </h2>
            </div>
            <Link to="/portfolio">
              <Button variant="outline" className="mt-4 sm:mt-0 border-slate-300 hover:border-blue-600 hover:text-blue-600">
                {t('buttons.viewAll')} {t('nav.portfolio')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioProjects.slice(0, 3).map((project) => (
              <Card key={project.id} className="group border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-4 group-hover:translate-y-0">
                    <span className="inline-block px-3 py-1 bg-blue-600 text-white text-sm rounded-full">
                      {project.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{project.title}</h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <a
                    href={project.demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 text-sm"
                  >
                    Live Demo
                    <ArrowRight className="ml-1" size={14} />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4">
              {t('home.testimonialsTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={testimonial.id} className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-amber-400 fill-amber-400" size={18} />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 leading-relaxed">
                    &ldquo;{t(`testimonials.testimonial${idx + 1}`)}&rdquo;
                  </p>
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-slate-500 text-sm">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            {t('home.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
                {t('buttons.contact')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/preise">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                {t('buttons.viewPrices')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
